import React from 'react';
import { Rocket, ChartLine, Sprout, HeartPulse, Map, Gavel } from 'lucide-react';
import UpgradePage from 'src/shared/templates/upgrade-page/UpgradePage';
import useProjectBreadcrumb from 'src/shared/hooks/useProjectBreadcrumb';
import useProject from 'src/shared/hooks/useProject';
import analyticsTracking from 'src/services/analytics-service';
import speciesInsightsUpgradeHero from 'src/assets/images/species-insights/species-insights-upgrade-hero.webp';

/** Species Insights' own HubSpot form (portal/region match the shared default). */
const SPECIES_INSIGHTS_HUBSPOT_FORM_ID = '51e7950f-1853-4c81-98b3-c283da587706';

const FEATURES = [
    {
        icon: Rocket,
        title: 'Automated Data Analysis Pipeline',
        body: 'Eliminate manual data processing to save time and budget.',
    },
    {
        icon: ChartLine,
        title: 'Nature Performance Dashboard',
        body: 'Evidence progress toward science-based targets with auditable tracking of IUCN and invasive species.',
    },
    {
        icon: Sprout,
        title: 'Ecology Hub Visualisations',
        body: 'Win board buy-in using automated heat trees and community comparisons that simplify complex data.',
    },
    {
        icon: HeartPulse,
        title: 'Specialised Health Indicators',
        body: 'Go beyond species lists with deep-dive metrics like Soil Fungal:Bacterial ratios.',
    },
    {
        icon: Map,
        title: 'Interactive Species Map',
        body: 'Visualise risk hotspots by filtering sampling data by geographical location and conservation status.',
    },
    {
        icon: Gavel,
        title: 'Regulatory Alignment',
        body: 'Future-proof your operations against evolving TNFD and CSRD disclosure requirements.',
    },
];

const FreemiumSpeciesInsights = () => {
    const breadcrumbs = useProjectBreadcrumb('Species Insights');
    const { currentProjectDetails } = useProject();

    const handleBookCallClick = () =>
        analyticsTracking.trackEvent({
            action: 'upgrade_subscription_click_from_species_insights',
            category: 'upgrade-subscription-link',
            project: currentProjectDetails?.projectName,
        });

    return (
        <UpgradePage
            testId='species-insights-upgrade-page'
            breadcrumbs={breadcrumbs}
            heroImage={speciesInsightsUpgradeHero}
            badgeLabel='Upgrade to Species Insights'
            title='From Raw Data to Nature Intelligence'
            description="Don't just detect species – understand them. Upgrading transforms your raw eDNA sequences into an automated pipeline of science-based metrics, built for smarter decisions and high-impact reporting."
            features={FEATURES}
            ctaTitle='Ready to see the full picture?'
            ctaDescription="Upgrade to Species Insights and start comparing sites, track trends, and evidence your impact with the world's most advanced eDNA intelligence platform."
            hubSpotForm={{ formId: SPECIES_INSIGHTS_HUBSPOT_FORM_ID }}
            onBookCallClick={handleBookCallClick}
        />
    );
};

export default FreemiumSpeciesInsights;
